import { memo, useCallback, useState } from 'react';
import { Dialog } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { uploloadCompanyImage } from '../../../../api/talentsApi';
import { useCreateNewManualCompany } from '../../../../hooks/useTalentCompany';
import STRING_KEYS from '../../../../language/keys';
import { getServerError } from '../../../../utils';
import ModalActions from './ModalActions';
import ModalContent from './ModalContent';
import ModalTitle from './ModatTitle';

type ManualCompanyModalProps = {
    isOpen: boolean,
    onCloseCreateNewCompanyModal: () => void,
    selectedId: string,
    companyFieldName: string,
    onCompanyCreated: (updatedObject:
      UpdateValues<string | boolean | null | DateObject>, id: string) => void,
}

const ManualCompanyModal = ({
  isOpen,
  onCloseCreateNewCompanyModal,
  selectedId,
  onCompanyCreated,
  companyFieldName,
}: ManualCompanyModalProps):JSX.Element => {
  const { t: translate } = useTranslation();
  const [companyName, setCompanyName] = useState<string>(companyFieldName);
  const [companyImage, setCompanyImage] = useState<string>('');
  const [isUploading, setIsUploadLoading] = useState<boolean>(false);
  const [serverError, setError] = useState<
  {hasError: boolean, errorMessage: string, }>({
    hasError: false,
    errorMessage: '',
  });
  const [isFieldError, setIsFieldError] = useState<boolean>(false);

  const handleCloseModal = () => {
    onCloseCreateNewCompanyModal();
    setCompanyName('');
    setCompanyImage('');
  };

  const createNewCompanyManually = useCreateNewManualCompany(() => {
    onCompanyCreated({
      companyName,
      companyLogo: companyImage,
      companyId: '',
    }, selectedId);
    onCloseCreateNewCompanyModal();
  },
  setError,
  );

  const handleChangeCompanyName = (enteredCompanyName: string) => {
    setCompanyName(enteredCompanyName);
    if (enteredCompanyName.trim() === '') {
      setIsFieldError(true);
    } else {
      setIsFieldError(false);
    }
  };

  const handleCreateCompany = () => {
    createNewCompanyManually.mutate({
      name: companyName,
      logo: companyImage,
    });
  };

  const handleInputUploadImage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const myFile = e.target.files ? e.target.files[0] : '';
    const formData = new FormData();
    formData.append('file', myFile);
    setIsUploadLoading(true);
    uploloadCompanyImage(formData).then((data: { fileUrl: string }) => {
      setCompanyImage(data.fileUrl);
    })
      .catch((error) => {
        const errorMessage = getServerError(error);
        setError({
          hasError: true,
          errorMessage,
        },
        );
      })
      .finally(() => {
        setIsUploadLoading(false);
      });
  }, [setIsUploadLoading, setError]);

  return (
    <Dialog open={ isOpen } onClose={ handleCloseModal }>
      <ModalTitle
        title={ translate(STRING_KEYS.CREATE_NEW_COMPANY) }
        onCloseModal={ handleCloseModal }
      />
      <ModalContent
        isLoading={ createNewCompanyManually.isLoading }
        isError={ serverError.hasError }
        errorMessage={ serverError.errorMessage }
        isUploadingImage={ isUploading }
        onUploadImage={ handleInputUploadImage }
        companyImage={ companyImage }
        isFieldError={ isFieldError }
        companyName={ companyName }
        onChangeCompanyName={ handleChangeCompanyName }
      />
     
      <ModalActions
        onCreateCompany={ handleCreateCompany }
        onCloseModal={ handleCloseModal }
        isDisabled={ companyName.trim() === '' }
      />
    </Dialog>
  );
};

export default memo(ManualCompanyModal);
